import { BASE_PATH, PING_FRAME, type ClientMsg, type ServerMsg } from "../src/protocol";
import { backoffDelay } from "./timing";

/**
 * connecting: first attempt. reconnecting: lost the socket, retrying with backoff.
 * stopped: gave up for good (fatal room error or explicit stop).
 */
export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "stopped";

export interface ConnectionHandlers {
  onMessage(msg: ServerMsg): void;
  onStatus(status: ConnectionStatus): void;
}

const PING_INTERVAL_MS = 25_000;
/** No traffic for this long (pongs included) means the socket is dead even if it looks open. */
const STALE_AFTER_MS = PING_INTERVAL_MS * 2 + 5_000;
/** After waking up (tab visible / back online) a ping must be answered this fast. */
const WAKE_CHECK_MS = 5_000;
/** Close codes the server uses for ROOM_FULL / ROOM_NOT_FOUND: retrying cannot help. */
const FATAL_CLOSE_CODES = new Set([4003, 4004]);

/** One WebSocket to a room, reconnecting with exponential backoff (1s, 2s, 4s… 15s). */
export class Connection {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private everOpened = false;
  private stopped = false;
  private lastMessageAt = 0;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private wakeTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly url: string,
    private readonly handlers: ConnectionHandlers,
  ) {
    window.addEventListener("online", () => this.wake());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.wake();
    });
  }

  static roomUrl(code: string, playerId: string, name: string): string {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams({ pid: playerId, name });
    return `${protocol}//${location.host}${BASE_PATH}/api/rooms/${code}/ws?${params}`;
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    if (this.stopped) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.handlers.onStatus(this.everOpened ? "reconnecting" : "connecting");

    const ws = new WebSocket(this.url);
    this.ws = ws;

    // Every handler checks `this.ws === ws` so an abandoned socket can never act.
    ws.addEventListener("open", () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.everOpened = true;
      this.lastMessageAt = Date.now();
      clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.heartbeat(), PING_INTERVAL_MS);
      this.handlers.onStatus("open");
    });
    ws.addEventListener("message", (event) => {
      if (this.ws !== ws || typeof event.data !== "string") return;
      this.lastMessageAt = Date.now();
      try {
        this.handlers.onMessage(JSON.parse(event.data) as ServerMsg);
      } catch (err) {
        console.error("[SnakeBrawl] bad message", err);
      }
    });
    ws.addEventListener("close", (event) => {
      if (this.ws !== ws) return;
      if (FATAL_CLOSE_CODES.has(event.code)) this.stop();
      else this.dropAndRetry();
    });
  }

  send(msg: ClientMsg): boolean {
    if (!this.isOpen) return false;
    this.ws!.send(JSON.stringify(msg));
    return true;
  }

  /** Sends the exact keep-alive frame the room auto-answers without waking up. */
  private ping(): void {
    if (this.isOpen) this.ws!.send(PING_FRAME);
  }

  /** Stops for good: no more reconnection attempts. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.teardown();
    this.handlers.onStatus("stopped");
  }

  private heartbeat(): void {
    if (Date.now() - this.lastMessageAt > STALE_AFTER_MS) {
      this.dropAndRetry();
      return;
    }
    this.ping();
  }

  /** Tab became visible or network came back: verify the socket now instead of waiting. */
  private wake(): void {
    if (this.stopped) return;
    if (!this.ws) {
      // Waiting on a backoff timer: skip the wait.
      this.attempt = 0;
      this.connect();
      return;
    }
    if (!this.isOpen) return;
    const sentAt = Date.now();
    this.ping();
    clearTimeout(this.wakeTimer);
    this.wakeTimer = setTimeout(() => {
      if (this.lastMessageAt < sentAt) this.dropAndRetry();
    }, WAKE_CHECK_MS);
  }

  /** Abandons the current socket (even a half-dead one) and schedules a retry. */
  private dropAndRetry(): void {
    if (this.stopped) return;
    this.teardown();
    const delay = backoffDelay(this.attempt++);
    this.handlers.onStatus("reconnecting");
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  private teardown(): void {
    clearInterval(this.pingTimer);
    clearTimeout(this.wakeTimer);
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try {
        ws.close(1000, "client");
      } catch {
        // Ignore: we're discarding this socket anyway.
      }
    }
  }
}
